/**
 * Strategy Lab — end-to-end repair (2026-05-16)
 *
 * Replaces the prior fixture-only module that:
 *   - Used browser alert() for "Strategy generated" / "Backtest completed".
 *   - Rendered hard-coded performance stats (+42.5% / 8.4% / 2.14 / 64.2%).
 *   - Had no cross-tab flow between AI Generator and Backtest Engine.
 *   - Had a placeholder equity-curve icon and fake replay P&L.
 *
 * This rewrite:
 *   - Lifts state into the parent so generated strategies flow into
 *     Backtest Engine for selection.
 *   - Removes alert() calls. Replaces them with in-page <NoticeBar>.
 *   - Wires Backtest to POST /api/v1/backtests/run and renders ONLY
 *     fields the backend actually returns.
 *   - Wires Trade Replay to POST /api/v1/backtests/replay; falls back
 *     to a truthful unavailable state if the request fails.
 *   - Detects the known backend fixture shape (trades=1, pnl=0.25,
 *     profit_factor>1e6) and labels results as "Sample / fixture data".
 *   - Disables AI Optimize until a real backend exists ("not connected").
 *   - "Apply" on AI Suggestions creates a StrategyDraft and labels the
 *     action as UI-only — it does not enable live trading or execution.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Cpu,
  ClipboardList,
  History,
  Play,
  Zap,
  Plus,
  CheckCircle2,
  BarChart3,
  Settings,
  Info,
  AlertTriangle,
  Pause,
  FastForward,
  Rewind,
  X,
  Inbox,
  ShieldOff,
} from 'lucide-react';
import PaperSessionReviewModule from './PaperSessionReviewModule';
import { motion } from 'motion/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { cn, logInteraction } from '../lib/utils';
import { apiV1Base } from '../lib/apiConfig';

// ─────────────────────────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────────────────────────

type StrategyTab = 'generator' | 'backtest' | 'simulation' | 'owner-review';

interface Condition {
  id: string;
  indicator: string;
  rule: string;
  value: string;
}

interface StrategyDraft {
  id: string;
  name: string;
  description: string;
  entry: Condition[];
  exit: Condition[];
  createdAt: number;
  source: 'manual' | 'suggestion';
}

interface BacktestMetrics {
  trades?: number;
  pnl?: number;
  win_rate?: number;
  profit_factor?: number;
  expectancy?: number;
  max_drawdown?: number;
}

interface AttributionEdge {
  condition: string;
  pnl: number;
}

interface BacktestReport {
  backtest_id: string;
  summary: string;
  metrics: BacktestMetrics;
  attribution?: {
    best_edges?: AttributionEdge[];
    worst_edges?: AttributionEdge[];
    promoted_conditions?: string[];
    recommendations?: string[];
  };
  promotion_gate?: {
    approved?: boolean;
    approved_for?: string;
    rejection_reasons?: string[];
  };
  recommendations?: string[];
}

// Rule-metadata shape returned by the backend rule adapter (parsed_rules /
// unsupported_rules / warnings). Optional everywhere because legacy
// fixture responses don't carry it.
type StrategyRuleMode = 'parsed' | 'partial' | 'default';
type StrategyEngineUsed = 'rules' | 'default_sma';

interface ParsedRuleSummary {
  side: string;
  kind: string;
  params?: Record<string, unknown>;
  raw?: { indicator?: string; rule?: string; value?: string };
}

interface UnsupportedRuleSummary {
  side: string;
  raw?: { indicator?: string; rule?: string; value?: string };
  reason: string;
}

interface RuleMetadata {
  mode?: StrategyRuleMode;
  engine?: StrategyEngineUsed;
  parsed?: { entry: ParsedRuleSummary[]; exit: ParsedRuleSummary[] };
  unsupported?: UnsupportedRuleSummary[];
  warnings?: string[];
}

interface BacktestResult {
  strategyName: string;
  report: BacktestReport;
  isFixture: boolean;
  fetchedAt: number;
  rules?: RuleMetadata;
}

type BacktestStatus = 'idle' | 'running' | 'success' | 'error';

interface ReplayEnvelope {
  events?: number;
  timelineCount: number;
  eventTypes: Record<string, number>;
  blockedTrades?: number;
  reasonCodes: string[];
  isFixture: boolean;
  fetchedAt: number;
  rules?: RuleMetadata;
}

type ReplayStatus = 'idle' | 'running' | 'success' | 'error' | 'unavailable';

interface Notice {
  id: string;
  kind: 'success' | 'info' | 'error';
  text: string;
}

// ─────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────

const API_KEY: string = (() => {
  try {
    return ((import.meta as unknown) as { env?: Record<string, string> })
      .env?.VITE_API_KEY ?? 'adversyn-dev-key-2026';
  } catch {
    return 'adversyn-dev-key-2026';
  }
})();

const FETCH_TIMEOUT_MS = 15_000;

async function postJson<T = unknown>(path: string, body: unknown): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const resp = await fetch(`${apiV1Base()}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!resp.ok) {
      throw new Error(`HTTP ${resp.status}`);
    }
    const data = (await resp.json()) as { ok?: boolean; data?: T; error?: { message?: string } };
    if (data && data.ok === false) {
      throw new Error(data.error?.message || 'Backend returned ok=false');
    }
    if (data && data.ok === true && data.data !== undefined) {
      return data.data;
    }
    return data as unknown as T;
  } finally {
    clearTimeout(timer);
  }
}

function makeLocalId(prefix: string): string {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now().toString(36)}_${rand}`;
}

/**
 * Convert local UI Condition records into the backend wire shape
 * `{indicator, rule, value}`. Drops empty rows and the local `id`.
 */
function serializeConditions(conds: Condition[]): { indicator: string; rule: string; value: string }[] {
  return conds
    .filter((c) => (c.indicator ?? '').trim().length > 0)
    .map((c) => ({
      indicator: c.indicator.trim(),
      rule: (c.rule ?? '').trim(),
      value: (c.value ?? '').trim(),
    }));
}

/**
 * Emit a single in-page notice describing how the backend handled the
 * rules — keeps the user honest about parsed vs partial vs default mode
 * without redundant chatter when the backend stayed silent.
 */
function emitRuleModeNotice(
  pushNotice: (kind: Notice['kind'], text: string) => void,
  rules: RuleMetadata | undefined,
): void {
  const mode = rules?.mode;
  if (!mode) return;
  if (mode === 'parsed') {
    pushNotice('success', 'Rules parsed and applied.');
  } else if (mode === 'partial') {
    pushNotice(
      'error',
      'Some rules are not supported yet. See the Rule Engine panel for details.',
    );
  } else if (mode === 'default') {
    pushNotice('info', 'Default simulator rules were used.');
  }
}

function isFixtureReport(r: BacktestReport | undefined | null): boolean {
  const m = r?.metrics;
  if (!m) return false;
  return (
    m.trades === 1 &&
    Math.abs((m.pnl ?? 0) - 0.25) < 1e-9 &&
    (m.profit_factor ?? 0) > 1e6
  );
}

function formatRelative(ts: number, now: number = Date.now()): string {
  const diff = Math.max(0, now - ts);
  if (diff < 60_000) return 'just now';
  const min = Math.round(diff / 60_000);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const day = Math.round(hr / 24);
  return `${day} d ago`;
}

function formatPct(v: number | undefined, digits = 1): string {
  if (v === undefined || v === null || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(digits)}%`;
}

function formatNum(v: number | undefined, digits = 2): string {
  if (v === undefined || v === null || Number.isNaN(v)) return '—';
  return v.toFixed(digits);
}

function formatPnl(v: number | undefined): string {
  if (v === undefined || v === null || Number.isNaN(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}$${Math.abs(v).toFixed(2)}`;
}

function strategySlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60) || 'strategy';
}

// ─────────────────────────────────────────────────────────────────
//  In-page notice bar (replaces alert())
// ─────────────────────────────────────────────────────────────────

function NoticeBar({
  notice,
  onDismiss,
}: {
  notice: Notice;
  onDismiss: (id: string) => void;
}) {
  const palette = {
    success: 'border-emerald-600/40 bg-emerald-600/10 text-emerald-700',
    info: 'border-sky-600/40 bg-sky-600/10 text-sky-700',
    error: 'border-rose-600/40 bg-rose-600/10 text-rose-700',
  }[notice.kind];
  const Icon =
    notice.kind === 'success'
      ? CheckCircle2
      : notice.kind === 'error'
      ? AlertTriangle
      : Info;
  return (
    <div
      role={notice.kind === 'error' ? 'alert' : 'status'}
      data-testid={`strategy-notice-${notice.kind}`}
      className={cn(
        'flex items-start gap-3 rounded-xl border px-4 py-3 text-xs font-bold',
        palette,
      )}
    >
      <Icon size={16} className="mt-0.5 shrink-0" />
      <div className="flex-1 leading-relaxed">{notice.text}</div>
      <button
        type="button"
        onClick={() => onDismiss(notice.id)}
        className="text-current opacity-60 hover:opacity-100"
        aria-label="Dismiss notice"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  Suggestion templates (UI-only, clearly labeled)
// ─────────────────────────────────────────────────────────────────

interface Suggestion {
  id: string;
  title: string;
  desc: string;
  entry: Omit<Condition, 'id'>[];
  exit: Omit<Condition, 'id'>[];
}

const SUGGESTIONS: Suggestion[] = [
  {
    id: 's1',
    title: 'Momentum Breakout',
    desc: 'Template — high-volume breakouts in large-cap tech.',
    entry: [
      { indicator: 'Volume', rule: '>', value: '2x Avg' },
      { indicator: 'Price', rule: 'Breakout', value: '20D High' },
    ],
    exit: [{ indicator: 'Trailing Stop', rule: ':', value: '2%' }],
  },
  {
    id: 's2',
    title: 'Mean Reversion',
    desc: 'Template — oversold S&P 500 names with bullish MACD crossover.',
    entry: [
      { indicator: 'Bollinger', rule: 'Below', value: 'Lower Band' },
      { indicator: 'MACD', rule: 'Bullish', value: 'Crossover' },
    ],
    exit: [{ indicator: 'RSI', rule: '>', value: '70' }],
  },
];

// ─────────────────────────────────────────────────────────────────
//  Parent module — owns Strategy Lab state
// ─────────────────────────────────────────────────────────────────

export default function StrategyLabModule() {
  const [activeTab, setActiveTab] = useState<StrategyTab>('generator');

  // Cross-tab state
  const [strategies, setStrategies] = useState<StrategyDraft[]>([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(null);

  // Backtest state
  const [backtestStatus, setBacktestStatus] = useState<BacktestStatus>('idle');
  const [backtestResult, setBacktestResult] = useState<BacktestResult | null>(null);
  const [backtestError, setBacktestError] = useState<string | null>(null);

  // Replay state
  const [replayStatus, setReplayStatus] = useState<ReplayStatus>('idle');
  const [replayResult, setReplayResult] = useState<ReplayEnvelope | null>(null);
  const [replayError, setReplayError] = useState<string | null>(null);

  // In-page notices
  const [notices, setNotices] = useState<Notice[]>([]);

  const pushNotice = useCallback((kind: Notice['kind'], text: string) => {
    setNotices((prev) => [
      ...prev.slice(-2),
      { id: makeLocalId('n'), kind, text },
    ]);
  }, []);

  const dismissNotice = useCallback((id: string) => {
    setNotices((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const handleTabChange = (tab: StrategyTab) => {
    if (tab === activeTab) return;
    logInteraction('StrategyLab', 'Change Tab', { tab });
    setActiveTab(tab);
  };

  const addStrategy = useCallback(
    (draft: StrategyDraft) => {
      setStrategies((prev) => [draft, ...prev].slice(0, 20));
      setSelectedStrategyId(draft.id);
    },
    [],
  );

  const selectedStrategy = useMemo(
    () => strategies.find((s) => s.id === selectedStrategyId) ?? null,
    [strategies, selectedStrategyId],
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'owner-review':
        return <PaperSessionReviewModule />;
      case 'generator':
        return (
          <AIStrategyGenerator
            onSaveStrategy={(draft) => {
              addStrategy(draft);
              pushNotice(
                'success',
                `Strategy "${draft.name}" saved to Strategy Lab. Switch to Backtest Engine to run it.`,
              );
            }}
            onApplyTemplate={(name) =>
              pushNotice(
                'info',
                `Template "${name}" loaded into Strategy Builder. Applies to UI only — does not enable live trading.`,
              )
            }
            onNotice={pushNotice}
          />
        );
      case 'backtest':
        return (
          <BacktestingEngine
            strategies={strategies}
            selectedStrategy={selectedStrategy}
            onSelectStrategy={setSelectedStrategyId}
            status={backtestStatus}
            result={backtestResult}
            error={backtestError}
            onRun={async (config) => {
              if (!selectedStrategy) return;
              logInteraction('Backtest', 'Run Backtest', config);
              setBacktestStatus('running');
              setBacktestError(null);
              try {
                const payload = {
                  strategy: strategySlug(selectedStrategy.name),
                  symbol: config.symbol,
                  start_date: config.startDate,
                  end_date: config.endDate,
                  initial_capital: config.initialCapital,
                  benchmark: config.benchmark,
                  entry_conditions: serializeConditions(selectedStrategy.entry),
                  exit_conditions: serializeConditions(selectedStrategy.exit),
                };
                const data = await postJson<{
                  backtest_id: string;
                  report: BacktestReport;
                  strategy_rule_mode?: StrategyRuleMode;
                  engine_used?: StrategyEngineUsed;
                  parsed_rules?: { entry: ParsedRuleSummary[]; exit: ParsedRuleSummary[] };
                  unsupported_rules?: UnsupportedRuleSummary[];
                  warnings?: string[];
                }>('/backtests/run', payload);
                const report = data.report || (data as unknown as BacktestReport);
                const fixture = isFixtureReport(report);
                const rules: RuleMetadata = {
                  mode: data.strategy_rule_mode,
                  engine: data.engine_used,
                  parsed: data.parsed_rules,
                  unsupported: data.unsupported_rules,
                  warnings: data.warnings,
                };
                setBacktestResult({
                  strategyName: selectedStrategy.name,
                  report,
                  isFixture: fixture,
                  fetchedAt: Date.now(),
                  rules,
                });
                setBacktestStatus('success');
                pushNotice(
                  fixture ? 'info' : 'success',
                  fixture
                    ? `Backtest returned sample fixture data. Real-strategy backtests will appear once the backend is wired to your strategy.`
                    : `Backtest complete for "${selectedStrategy.name}".`,
                );
                emitRuleModeNotice(pushNotice, rules);
              } catch (err) {
                const msg = err instanceof Error ? err.message : String(err);
                setBacktestError(msg);
                setBacktestStatus('error');
                pushNotice('error', `Backtest failed: ${msg}`);
              }
            }}
          />
        );
      case 'simulation':
        return (
          <TradeReplaySimulator
            status={replayStatus}
            result={replayResult}
            error={replayError}
            selectedStrategy={selectedStrategy}
            onLoad={async (cfg) => {
              logInteraction('TradeReplay', 'Load Replay', cfg);
              setReplayStatus('running');
              setReplayError(null);
              try {
                const data = await postJson<{
                  events?: number;
                  timeline?: Array<{ event_type?: string }>;
                  blocked_trades?: number;
                  reason_codes?: string[];
                  strategy_rule_mode?: StrategyRuleMode;
                  engine_used?: StrategyEngineUsed;
                  parsed_rules?: { entry: ParsedRuleSummary[]; exit: ParsedRuleSummary[] };
                  unsupported_rules?: UnsupportedRuleSummary[];
                  warnings?: string[];
                }>('/backtests/replay', {
                  symbol: cfg.symbol,
                  start_date: cfg.startDate,
                  end_date: cfg.endDate,
                  strategy: selectedStrategy ? strategySlug(selectedStrategy.name) : undefined,
                  entry_conditions: selectedStrategy ? serializeConditions(selectedStrategy.entry) : undefined,
                  exit_conditions: selectedStrategy ? serializeConditions(selectedStrategy.exit) : undefined,
                });
                const timeline = data.timeline ?? [];
                const eventTypes: Record<string, number> = {};
                for (const ev of timeline) {
                  const t = ev.event_type ?? 'UNKNOWN';
                  eventTypes[t] = (eventTypes[t] ?? 0) + 1;
                }
                const rules: RuleMetadata = {
                  mode: data.strategy_rule_mode,
                  engine: data.engine_used,
                  parsed: data.parsed_rules,
                  unsupported: data.unsupported_rules,
                  warnings: data.warnings,
                };
                const env: ReplayEnvelope = {
                  events: data.events,
                  timelineCount: timeline.length,
                  eventTypes,
                  blockedTrades: data.blocked_trades,
                  reasonCodes: (data.reason_codes ?? []).slice(0, 8),
                  isFixture:
                    timeline.length > 0 &&
                    typeof timeline[0] === 'object' &&
                    (timeline[0] as { event_id?: string }).event_id?.startsWith(
                      'fixture-',
                    ) === true,
                  fetchedAt: Date.now(),
                  rules,
                };
                setReplayResult(env);
                setReplayStatus('success');
                emitRuleModeNotice(pushNotice, rules);
                pushNotice(
                  env.isFixture ? 'info' : 'success',
                  env.isFixture
                    ? `Replay returned sample fixture timeline (${env.timelineCount} events).`
                    : `Replay loaded: ${env.timelineCount} events.`,
                );
              } catch (err) {
                const msg = err instanceof Error ? err.message : String(err);
                setReplayError(msg);
                setReplayStatus('unavailable');
                pushNotice(
                  'error',
                  `Trade Replay is not available right now: ${msg}`,
                );
              }
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="app-container space-y-8"
      data-testid="strategy-lab-root"
    >
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-[var(--app-text)] tracking-tight uppercase italic font-display">
            Strategy Lab
          </h1>
          <p className="text-[var(--muted-text)] mt-1 font-medium italic">
            Build strategies, run backtests, and replay historical sessions.
            Read-only / paper-only — does not place trades or change live
            settings.
          </p>
        </div>
        <div className="flex bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl p-1 shadow-sm">
          {(
            [
              { id: 'generator', label: 'AI Generator', icon: Cpu },
              { id: 'backtest', label: 'Backtest Engine', icon: History },
              { id: 'simulation', label: 'Trade Replay', icon: Play },
              { id: 'owner-review', label: 'Owner Review', icon: ClipboardList },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              data-testid={`strategy-tab-${tab.id}`}
              onClick={() => handleTabChange(tab.id)}
              className={cn(
                'px-4 py-2 rounded-lg text-[10px] font-black uppercase italic transition-all flex items-center gap-2 border',
                activeTab === tab.id
                  ? 'bg-[var(--app-text)] text-[var(--card-bg)] shadow-[0_0_10px_rgba(var(--app-text-rgb),0.2)] border-[var(--app-text)]'
                  : 'text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-border)] border-transparent',
              )}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {notices.length > 0 && (
        <div
          className="space-y-2"
          data-testid="strategy-notices"
        >
          {notices.map((n) => (
            <NoticeBar key={n.id} notice={n} onDismiss={dismissNotice} />
          ))}
        </div>
      )}

      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {renderContent()}
      </motion.div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  AI Generator
// ─────────────────────────────────────────────────────────────────

interface StrategyOptimization {
  status: 'ok' | 'warning' | 'error';
  code?: string;
  message?: string;
  optimized_strategy?: {
    name?: string;
    entry_conditions?: { indicator?: string; rule?: string; value?: string }[];
    exit_conditions?: { indicator?: string; rule?: string; value?: string }[];
    risk_controls?: string[];
    position_sizing?: string;
    timeframe?: string;
    notes?: string[];
  } | null;
  ai_recommendations?: {
    type: 'entry' | 'exit' | 'risk' | 'filter' | 'position_sizing' | 'data_requirement';
    recommendation: string;
    reason: string;
    confidence: number;
  }[];
  warnings?: string[];
  missing_data?: string[];
  advisory_only: boolean;
  paper_only: boolean;
  advisory_note?: string;
}


function AIStrategyGenerator({
  onSaveStrategy,
  onApplyTemplate,
  onNotice,
}: {
  onSaveStrategy: (draft: StrategyDraft) => void;
  onApplyTemplate: (name: string) => void;
  onNotice: (kind: Notice['kind'], text: string) => void;
}) {
  const [strategyName, setStrategyName] = useState('My Strategy');
  const [strategyDesc, setStrategyDesc] = useState('');
  const [entryConditions, setEntryConditions] = useState<Condition[]>([
    { id: makeLocalId('c'), indicator: 'RSI (14)', rule: 'Oversold <', value: '30' },
    { id: makeLocalId('c'), indicator: 'EMA 50', rule: 'Cross Above', value: 'EMA 200' },
  ]);
  const [exitConditions, setExitConditions] = useState<Condition[]>([
    { id: makeLocalId('c'), indicator: 'Take Profit', rule: ':', value: '+15%' },
    { id: makeLocalId('c'), indicator: 'Stop Loss', rule: ':', value: '-5%' },
  ]);

  // 2026-05-20 Strategy Lab AI Optimization — connected to POST
  //   /api/v1/strategy-lab/optimize
  // Advisory-only, paper-only. Deterministic backend; no broker calls.
  const [optimizing, setOptimizing] = useState(false);
  const [optimizationError, setOptimizationError] = useState<string | null>(null);
  const [optimization, setOptimization] = useState<StrategyOptimization | null>(null);

  const trimmedName = strategyName.trim();
  const hasEntry = entryConditions.some((c) => c.indicator.trim());
  const hasExit = exitConditions.some((c) => c.indicator.trim());
  const canSave = trimmedName.length > 0 && hasEntry && hasExit;

  const handleOptimize = useCallback(async () => {
    setOptimizing(true);
    setOptimizationError(null);
    try {
      const body = {
        strategy_name: strategyName,
        description: strategyDesc,
        entry_conditions: entryConditions.map((c) => ({
          id: c.id,
          indicator: c.indicator,
          rule: c.rule,
          value: c.value,
        })),
        exit_conditions: exitConditions.map((c) => ({
          id: c.id,
          indicator: c.indicator,
          rule: c.rule,
          value: c.value,
        })),
        // The frontend doesn't yet expose asset_class / risk_profile pickers
        // on the AI Co-Pilot card. Default to safe values that the backend
        // optimizer handles. Future task: surface these selectors here.
        asset_class: 'stocks',
        timeframe: '1d',
        risk_profile: 'moderate',
      };
      const result = await postJson<StrategyOptimization>(
        '/strategy-lab/optimize',
        body,
      );
      setOptimization(result);
      onNotice(
        result.status === 'error' ? 'error' : 'info',
        result.status === 'error'
          ? `Optimizer validation: ${result.message ?? 'check inputs'}`
          : `Strategy optimized — ${result.ai_recommendations?.length ?? 0} recommendations.`,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setOptimizationError(msg);
      onNotice('error', `Optimizer unavailable: ${msg}`);
    } finally {
      setOptimizing(false);
    }
  }, [
    strategyName,
    strategyDesc,
    entryConditions,
    exitConditions,
    onNotice,
  ]);

  const addCondition = (type: 'entry' | 'exit') => {
    const c: Condition = { id: makeLocalId('c'), indicator: '', rule: '', value: '' };
    if (type === 'entry') setEntryConditions((p) => [...p, c]);
    else setExitConditions((p) => [...p, c]);
  };

  const removeCondition = (id: string, type: 'entry' | 'exit') => {
    if (type === 'entry') setEntryConditions((p) => p.filter((c) => c.id !== id));
    else setExitConditions((p) => p.filter((c) => c.id !== id));
  };

  const updateCondition = (
    id: string,
    field: keyof Condition,
    value: string,
    type: 'entry' | 'exit',
  ) => {
    const upd = (prev: Condition[]) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c));
    if (type === 'entry') setEntryConditions(upd);
    else setExitConditions(upd);
  };

  const handleSave = () => {
    logInteraction('StrategyGenerator', 'Save Strategy', { name: trimmedName });
    if (!canSave) {
      onNotice('error', 'Name, at least one entry condition, and at least one exit condition are required.');
      return;
    }
    const cleanEntry = entryConditions.filter((c) => c.indicator.trim());
    const cleanExit = exitConditions.filter((c) => c.indicator.trim());
    onSaveStrategy({
      id: makeLocalId('s'),
      name: trimmedName,
      description: strategyDesc.trim(),
      entry: cleanEntry,
      exit: cleanExit,
      createdAt: Date.now(),
      source: 'manual',
    });
  };

  const applyTemplate = (s: Suggestion) => {
    logInteraction('StrategyGenerator', 'Apply Template', { template: s.title });
    setEntryConditions(
      s.entry.map((c) => ({ ...c, id: makeLocalId('c') })),
    );
    setExitConditions(
      s.exit.map((c) => ({ ...c, id: makeLocalId('c') })),
    );
    setStrategyName(s.title);
    setStrategyDesc(s.desc);
    onApplyTemplate(s.title);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="strategy-generator-root">
      <div className="lg:col-span-8 space-y-6">
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
            <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic flex items-center gap-2">
              <Settings size={20} className="text-emerald-600" /> Strategy Builder
            </h3>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              data-testid="strategy-btn-generate"
              aria-disabled={!canSave}
              className={cn(
                'px-6 py-2 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase italic transition-all shadow-lg shadow-emerald-600/20',
                canSave
                  ? 'hover:bg-emerald-700'
                  : 'opacity-50 cursor-not-allowed',
              )}
            >
              Save Strategy
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
                Strategy Name
              </label>
              <input
                type="text"
                value={strategyName}
                onChange={(e) => setStrategyName(e.target.value)}
                data-testid="strategy-input-name"
                className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-black text-[var(--app-text)] outline-none"
                placeholder="e.g. Momentum Breakout v1"
                maxLength={80}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
                Description (optional)
              </label>
              <input
                type="text"
                value={strategyDesc}
                onChange={(e) => setStrategyDesc(e.target.value)}
                data-testid="strategy-input-desc"
                className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-medium text-[var(--app-text)] outline-none"
                placeholder="Short description"
                maxLength={160}
              />
            </div>
          </div>

          <div className="space-y-8">
            <ConditionList
              kind="entry"
              conditions={entryConditions}
              onAdd={() => addCondition('entry')}
              onRemove={(id) => removeCondition(id, 'entry')}
              onUpdate={(id, f, v) => updateCondition(id, f, v, 'entry')}
            />
            <ConditionList
              kind="exit"
              conditions={exitConditions}
              onAdd={() => addCondition('exit')}
              onRemove={(id) => removeCondition(id, 'exit')}
              onUpdate={(id, f, v) => updateCondition(id, f, v, 'exit')}
            />
          </div>
        </div>

        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic">
              AI Strategy Suggestions
            </h3>
            <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase">
              Templates
            </span>
          </div>
          <p className="text-[11px] text-[var(--muted-text)] mb-4">
            Templates only — clicking <em>Use Template</em> loads the conditions
            into the builder above. Does not place trades or change live
            settings.
          </p>
          <div className="space-y-4">
            {SUGGESTIONS.map((s) => (
              <div
                key={s.id}
                data-testid={`strategy-suggestion-${s.id}`}
                className="p-5 border border-[var(--card-border)] rounded-2xl flex items-center justify-between gap-4 hover:border-emerald-600/20 transition-all group"
              >
                <div className="max-w-md">
                  <h4 className="text-sm font-black text-[var(--app-text)] uppercase italic group-hover:text-emerald-600">
                    {s.title}
                  </h4>
                  <p className="text-[10px] text-[var(--muted-text)] font-medium mt-1">
                    {s.desc}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => applyTemplate(s)}
                  data-testid={`strategy-suggestion-apply-${s.id}`}
                  className="text-[10px] font-black text-emerald-700 uppercase italic px-3 py-1.5 rounded-lg border border-emerald-600/30 hover:bg-emerald-600/10 transition-all"
                >
                  Use Template
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="lg:col-span-4 space-y-6">
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 text-[var(--app-text)] shadow-xl shadow-black/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Zap size={20} className="fill-white" />
            </div>
            <div>
              <h3 className="text-sm font-black uppercase italic">AI Co-Pilot</h3>
              <p className="text-[10px] text-[var(--muted-text)] font-bold uppercase">
                Strategy Optimization
              </p>
            </div>
          </div>
          {/* 2026-05-20 Strategy Lab AI Optimizer — connected. */}
          {optimizationError && (
            <div
              className="flex items-start gap-2 rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-[11px] font-bold text-rose-700"
              data-testid="strategy-optimize-error"
            >
              <ShieldOff size={14} className="mt-0.5" />
              <span>Optimizer unavailable: {optimizationError}</span>
            </div>
          )}
          {!optimizationError && !optimization && (
            <div
              className="flex items-start gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-[11px] font-bold text-emerald-700"
              data-testid="strategy-optimize-ready"
            >
              <Zap size={14} className="mt-0.5" />
              <span>
                Run the AI optimizer to get advisory recommendations on entries,
                exits, risk controls, and position sizing. Paper-only.
              </span>
            </div>
          )}
          {optimization && optimization.status === 'error' && (
            <div
              className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] font-bold text-amber-700"
              data-testid="strategy-optimize-validation"
            >
              <ShieldOff size={14} className="mt-0.5" />
              <span>{optimization.message ?? 'Validation failed.'}</span>
            </div>
          )}
          {optimization && optimization.status !== 'error' && (
            <div
              className="space-y-3 max-h-[420px] overflow-y-auto pr-1"
              data-testid="strategy-optimize-results"
            >
              {optimization.optimized_strategy?.position_sizing && (
                <div className="rounded-xl border border-[var(--card-header-border)] bg-[var(--input-bg)] p-3">
                  <p className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Position Sizing</p>
                  <p className="text-[11px] text-[var(--app-text)] font-medium">{optimization.optimized_strategy.position_sizing}</p>
                </div>
              )}
              {(optimization.ai_recommendations ?? []).length > 0 && (
                <div className="rounded-xl border border-[var(--card-header-border)] bg-[var(--input-bg)] p-3">
                  <p className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-2">AI Recommendations</p>
                  <ul className="space-y-2" data-testid="strategy-optimize-recommendations">
                    {(optimization.ai_recommendations ?? []).map((r, i) => (
                      <li key={i} className="border border-[var(--input-border)] rounded-lg p-2">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700">{r.type}</span>
                          <span className="text-[9px] font-black text-[var(--muted-text)]">{r.confidence}% confidence</span>
                        </div>
                        <p className="text-[11px] text-[var(--app-text)] font-bold leading-relaxed">{r.recommendation}</p>
                        <p className="text-[10px] text-[var(--muted-text)] mt-1 leading-relaxed">{r.reason}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(optimization.warnings ?? []).length > 0 && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3">
                  <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-1">Warnings</p>
                  <ul className="space-y-1">
                    {(optimization.warnings ?? []).map((w, i) => (
                      <li key={i} className="text-[10px] text-amber-700 leading-relaxed">{w}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(optimization.missing_data ?? []).length > 0 && (
                <div className="rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] p-3">
                  <p className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Missing Data</p>
                  <ul className="space-y-1">
                    {(optimization.missing_data ?? []).map((m, i) => (
                      <li key={i} className="text-[10px] text-[var(--muted-text)] font-mono">{m}</li>
                    ))}
                  </ul>
                </div>
              )}
              {optimization.advisory_note && (
                <p className="text-[9px] text-[var(--muted-text)] italic leading-relaxed">{optimization.advisory_note}</p>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={handleOptimize}
            disabled={optimizing || !canSave}
            data-testid="strategy-btn-optimize"
            className={cn(
              "w-full mt-4 py-3 rounded-xl text-[10px] font-black uppercase italic transition-colors",
              optimizing || !canSave
                ? "bg-emerald-600/40 text-white/70 cursor-not-allowed"
                : "bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer"
            )}
          >
            {optimizing
              ? 'Optimizing…'
              : (!canSave ? 'Optimize Strategy (Define Conditions First)' : 'Optimize Strategy')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConditionList({
  kind,
  conditions,
  onAdd,
  onRemove,
  onUpdate,
}: {
  kind: 'entry' | 'exit';
  conditions: Condition[];
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, field: keyof Condition, value: string) => void;
}) {
  const accent = kind === 'entry' ? 'text-emerald-600' : 'text-rose-600';
  return (
    <div className="space-y-4">
      <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
        {kind === 'entry' ? 'Entry Conditions' : 'Exit Conditions'}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {conditions.map((condition) => (
          <div
            key={condition.id}
            data-testid={`strategy-condition-${kind}-${condition.id}`}
            className="p-4 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl flex items-center gap-2"
          >
            <input
              className="bg-transparent text-xs font-bold text-[var(--app-text)] uppercase outline-none flex-1 min-w-0"
              value={condition.indicator}
              onChange={(e) => onUpdate(condition.id, 'indicator', e.target.value)}
              placeholder="Indicator"
            />
            <input
              className="bg-transparent text-[10px] font-bold text-[var(--muted-text)] uppercase outline-none w-16"
              value={condition.rule}
              onChange={(e) => onUpdate(condition.id, 'rule', e.target.value)}
              placeholder="Rule"
            />
            <input
              className={cn(
                'bg-transparent text-xs font-bold uppercase outline-none w-16',
                accent,
              )}
              value={condition.value}
              onChange={(e) => onUpdate(condition.id, 'value', e.target.value)}
              placeholder="Value"
            />
            <button
              type="button"
              onClick={() => onRemove(condition.id)}
              className="text-[var(--muted-text)] hover:text-rose-600 transition-colors shrink-0"
              aria-label="Remove condition"
            >
              <Plus className="rotate-45" size={16} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={onAdd}
          data-testid={`strategy-add-${kind}`}
          className="p-4 border-2 border-dashed border-[var(--card-border)] rounded-xl flex items-center justify-center gap-2 text-[var(--muted-text)] hover:text-emerald-600 hover:border-emerald-600/20 transition-all"
        >
          <Plus size={16} />
          <span className="text-[10px] font-black uppercase italic">
            Add Condition
          </span>
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  Backtest Engine
// ─────────────────────────────────────────────────────────────────

interface BacktestConfig {
  symbol: string;
  startDate: string;
  endDate: string;
  initialCapital: number;
  benchmark: string;
}

function BacktestingEngine({
  strategies,
  selectedStrategy,
  onSelectStrategy,
  status,
  result,
  error,
  onRun,
}: {
  strategies: StrategyDraft[];
  selectedStrategy: StrategyDraft | null;
  onSelectStrategy: (id: string) => void;
  status: BacktestStatus;
  result: BacktestResult | null;
  error: string | null;
  onRun: (cfg: BacktestConfig) => void;
}) {
  const [symbol, setSymbol] = useState('AAPL');
  const [startDate, setStartDate] = useState('2024-01-01');
  const [endDate, setEndDate] = useState('2024-04-01');
  const [capitalInput, setCapitalInput] = useState('100000');
  const [benchmark, setBenchmark] = useState('S&P 500 (SPY)');

  const capitalNumber = useMemo(() => {
    const cleaned = capitalInput.replace(/[^\d.]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) && n > 0 ? n : NaN;
  }, [capitalInput]);

  const dateRangeValid =
    !!startDate && !!endDate && new Date(startDate) <= new Date(endDate);
  const canRun =
    !!selectedStrategy &&
    status !== 'running' &&
    Number.isFinite(capitalNumber) &&
    dateRangeValid;

  const handleRun = () => {
    if (!canRun || !selectedStrategy) return;
    onRun({
      symbol: symbol.trim() || 'AAPL',
      startDate,
      endDate,
      initialCapital: capitalNumber,
      benchmark,
    });
  };

  return (
    <div className="space-y-8" data-testid="backtest-engine-root">
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic flex items-center gap-2">
            <History size={20} className="text-emerald-600" /> Backtest Controls
          </h3>
          <button
            type="button"
            onClick={handleRun}
            disabled={!canRun}
            aria-disabled={!canRun}
            data-testid="strategy-btn-run-backtest"
            className={cn(
              'px-6 py-2 bg-[var(--app-text)] text-[var(--card-bg)] rounded-xl text-xs font-black uppercase italic transition-all',
              canRun
                ? 'hover:opacity-90'
                : 'opacity-50 cursor-not-allowed',
            )}
          >
            {status === 'running' ? 'Running...' : 'Run Backtest'}
          </button>
        </div>

        {strategies.length === 0 ? (
          <div
            className="rounded-xl border border-dashed border-[var(--card-border)] bg-[var(--input-bg)] px-4 py-6 text-center"
            data-testid="backtest-empty-strategies"
          >
            <Inbox className="mx-auto text-[var(--muted-text)] mb-2" size={28} />
            <p className="text-xs font-bold text-[var(--muted-text)]">
              No strategies yet. Go to <strong>AI Generator</strong> and save a
              strategy first.
            </p>
          </div>
        ) : (
          <div className="space-y-2 mb-6">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
              Strategy
            </label>
            <select
              value={selectedStrategy?.id ?? ''}
              onChange={(e) => onSelectStrategy(e.target.value)}
              data-testid="backtest-select-strategy"
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-black text-[var(--app-text)] outline-none"
            >
              {strategies.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.entry.length + s.exit.length > 0
                    ? `  (${s.entry.length}E / ${s.exit.length}X)`
                    : ''}
                </option>
              ))}
            </select>
            {selectedStrategy && (
              <p
                className="text-[10px] text-[var(--muted-text)]"
                data-testid="backtest-selected-meta"
              >
                Saved {formatRelative(selectedStrategy.createdAt)}
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="space-y-2 md:col-span-2">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
              Date Range
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="backtest-input-start"
                className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-xs font-bold text-[var(--app-text)] outline-none"
              />
              <span className="text-[var(--muted-text)]">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="backtest-input-end"
                className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-xs font-bold text-[var(--app-text)] outline-none"
              />
            </div>
            {!dateRangeValid && (
              <p className="text-[10px] text-rose-600 font-bold">
                Start date must be on or before end date.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
              Symbol
            </label>
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              data-testid="backtest-input-symbol"
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-black text-[var(--app-text)] outline-none"
              maxLength={12}
            />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
              Initial Capital (USD)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={capitalInput}
              onChange={(e) => setCapitalInput(e.target.value)}
              data-testid="backtest-input-capital"
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-black text-[var(--app-text)] outline-none"
            />
            {!Number.isFinite(capitalNumber) && (
              <p className="text-[10px] text-rose-600 font-bold">
                Enter a positive number.
              </p>
            )}
          </div>
          <div className="space-y-2 md:col-span-4">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
              Benchmark
            </label>
            <select
              value={benchmark}
              onChange={(e) => setBenchmark(e.target.value)}
              data-testid="backtest-input-benchmark"
              className="w-full md:w-72 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-black text-[var(--app-text)] outline-none"
            >
              <option>S&P 500 (SPY)</option>
              <option>NASDAQ 100 (QQQ)</option>
              <option>Russell 2000 (IWM)</option>
              <option>None</option>
            </select>
          </div>
        </div>
      </div>

      <BacktestResultsPanel status={status} result={result} error={error} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  Rule-metadata panel — shared by Backtest + Replay result panels.
// ─────────────────────────────────────────────────────────────────

const RULE_KIND_LABEL: Record<string, string> = {
  sma_cross_up: 'Fast SMA crosses above slow SMA',
  sma_cross_down: 'Fast SMA crosses below slow SMA',
  close_above_sma: 'Close above SMA',
  close_below_sma: 'Close below SMA',
  rsi_below: 'RSI below threshold',
  rsi_above: 'RSI above threshold',
  profit_target_pct: 'Profit target',
  stop_loss_pct: 'Stop loss',
};

function describeRule(r: ParsedRuleSummary): string {
  const label = RULE_KIND_LABEL[r.kind] ?? r.kind;
  const p = r.params ?? {};
  if (r.kind === 'sma_cross_up' || r.kind === 'sma_cross_down') {
    return `${label} (fast ${p.fast}, slow ${p.slow})`;
  }
  if (r.kind === 'close_above_sma' || r.kind === 'close_below_sma') {
    return `${label} ${p.window}`;
  }
  if (r.kind === 'rsi_below' || r.kind === 'rsi_above') {
    return `${label} ${p.threshold} (window ${p.window})`;
  }
  if (r.kind === 'profit_target_pct' || r.kind === 'stop_loss_pct') {
    const pct = typeof p.pct === 'number' ? `${(p.pct * 100).toFixed(1)}%` : `${p.pct}`;
    return `${label} ${pct}`;
  }
  return label;
}

function describeRaw(raw?: { indicator?: string; rule?: string; value?: string }): string {
  if (!raw) return '';
  return [raw.indicator, raw.rule, raw.value].filter(Boolean).join(' ').trim();
}

function RuleMetadataPanel({ rules, testIdPrefix }: { rules?: RuleMetadata; testIdPrefix: string }) {
  if (!rules || !rules.mode) return null;
  const palette: Record<NonNullable<StrategyRuleMode>, string> = {
    parsed: 'border-emerald-600/40 bg-emerald-600/10 text-emerald-700',
    partial: 'border-amber-500/40 bg-amber-500/10 text-amber-700',
    default: 'border-sky-600/40 bg-sky-600/10 text-sky-700',
  };
  const headline =
    rules.mode === 'parsed'
      ? 'Rules parsed and applied.'
      : rules.mode === 'partial'
      ? 'Some rules are not supported yet.'
      : 'Default simulator rules were used.';
  const parsedEntry = rules.parsed?.entry ?? [];
  const parsedExit = rules.parsed?.exit ?? [];
  const unsupported = rules.unsupported ?? [];
  const warnings = rules.warnings ?? [];
  return (
    <div
      data-testid={`${testIdPrefix}-rules-panel`}
      className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic">
          Rule Engine
        </h3>
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
          <span data-testid={`${testIdPrefix}-rules-engine`}>engine: {rules.engine ?? 'unknown'}</span>
          <span>·</span>
          <span data-testid={`${testIdPrefix}-rules-mode`}>mode: {rules.mode}</span>
        </div>
      </div>
      <div
        className={cn(
          'flex items-start gap-2 rounded-xl border px-3 py-2 text-[11px] font-bold',
          palette[rules.mode],
        )}
        data-testid={`${testIdPrefix}-rules-mode-banner`}
      >
        <Info size={14} className="mt-0.5" />
        <span>{headline}</span>
      </div>
      {(parsedEntry.length > 0 || parsedExit.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-2">
              Entry rules ({parsedEntry.length})
            </p>
            {parsedEntry.length > 0 ? (
              <ul
                className="space-y-1 text-xs text-[var(--app-text)] list-disc pl-5"
                data-testid={`${testIdPrefix}-rules-entry`}
              >
                {parsedEntry.map((r, i) => (
                  <li key={`e-${i}`}>{describeRule(r)}</li>
                ))}
              </ul>
            ) : (
              <p className="text-[11px] text-[var(--muted-text)] italic">None.</p>
            )}
          </div>
          <div>
            <p className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-2">
              Exit rules ({parsedExit.length})
            </p>
            {parsedExit.length > 0 ? (
              <ul
                className="space-y-1 text-xs text-[var(--app-text)] list-disc pl-5"
                data-testid={`${testIdPrefix}-rules-exit`}
              >
                {parsedExit.map((r, i) => (
                  <li key={`x-${i}`}>{describeRule(r)}</li>
                ))}
              </ul>
            ) : (
              <p className="text-[11px] text-[var(--muted-text)] italic">None.</p>
            )}
          </div>
        </div>
      )}
      {unsupported.length > 0 && (
        <div data-testid={`${testIdPrefix}-rules-unsupported`}>
          <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-2">
            Unsupported rules ({unsupported.length})
          </p>
          <ul className="space-y-2 text-xs text-[var(--muted-text)] list-disc pl-5">
            {unsupported.map((u, i) => (
              <li key={`u-${i}`}>
                <span className="text-[var(--app-text)] font-bold">{describeRaw(u.raw) || '(unspecified)'}</span>
                {u.reason ? ` — ${u.reason}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <ul
          className="space-y-1 text-[11px] text-amber-700 list-disc pl-5"
          data-testid={`${testIdPrefix}-rules-warnings`}
        >
          {warnings.map((w, i) => (
            <li key={`w-${i}`}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BacktestResultsPanel({
  status,
  result,
  error,
}: {
  status: BacktestStatus;
  result: BacktestResult | null;
  error: string | null;
}) {
  if (status === 'running') {
    return (
      <div
        data-testid="strategy-panel-results"
        className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm"
      >
        <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic mb-4">
          Backtest Result
        </h3>
        <div
          className="min-h-[200px] flex flex-col items-center justify-center gap-3"
          data-testid="backtest-status-running"
        >
          <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-xs font-bold text-[var(--muted-text)] uppercase">
            Running backtest...
          </p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        data-testid="strategy-panel-results"
        className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm"
      >
        <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic mb-4">
          Backtest Result
        </h3>
        <div
          className="min-h-[200px] flex flex-col items-center justify-center gap-3 text-rose-600"
          data-testid="backtest-status-error"
        >
          <AlertTriangle size={28} />
          <p className="text-xs font-bold uppercase">Backtest failed</p>
          {error && (
            <p className="text-[11px] font-medium text-[var(--muted-text)] max-w-md text-center">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (status === 'idle' || !result) {
    return (
      <div
        data-testid="strategy-panel-results"
        className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm"
      >
        <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic mb-4">
          Backtest Result
        </h3>
        <div
          className="min-h-[200px] flex flex-col items-center justify-center gap-3"
          data-testid="backtest-status-empty"
        >
          <BarChart3 className="text-[var(--muted-text)] opacity-30" size={40} />
          <p className="text-xs font-bold text-[var(--muted-text)] uppercase">
            No backtest results yet
          </p>
          <p className="text-[11px] text-[var(--muted-text)]">
            Configure a strategy and click <strong>Run Backtest</strong>.
          </p>
        </div>
      </div>
    );
  }

  const report = result.report;
  const m = report.metrics ?? {};
  const stats: { label: string; value: string; color: string }[] = [
    {
      label: 'Trades',
      value: m.trades !== undefined ? String(m.trades) : '—',
      color: 'text-[var(--app-text)]',
    },
    {
      label: 'Total P&L',
      value: formatPnl(m.pnl),
      color: (m.pnl ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600',
    },
    {
      label: 'Win Rate',
      value: formatPct(m.win_rate),
      color: 'text-[var(--app-text)]',
    },
    {
      label: 'Profit Factor',
      value: formatNum(m.profit_factor, 2),
      color: 'text-[var(--app-text)]',
    },
    {
      label: 'Expectancy',
      value: formatPnl(m.expectancy),
      color: 'text-[var(--app-text)]',
    },
    {
      label: 'Max Drawdown',
      value: formatPct(m.max_drawdown),
      color: 'text-rose-600',
    },
  ];

  const bestEdges = (report.attribution?.best_edges ?? []).slice(0, 6);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="strategy-panel-results">
      <div className="lg:col-span-8 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic">
            Backtest Result — {result.strategyName}
          </h3>
          <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase">
            {formatRelative(result.fetchedAt)}
          </span>
        </div>
        {result.isFixture && (
          <div
            className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] font-bold text-amber-700"
            data-testid="backtest-fixture-banner"
          >
            <Info size={14} className="mt-0.5" />
            <span>
              Backend returned a sample fixture result. These numbers reflect a
              backend demo payload, not your strategy.
            </span>
          </div>
        )}
        {report.summary && (
          <p className="text-xs text-[var(--muted-text)] leading-relaxed">
            {report.summary}
          </p>
        )}

        {bestEdges.length > 0 ? (
          <div data-testid="backtest-chart-attribution">
            <p className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-2">
              Attribution — Best Edges
            </p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={bestEdges.map((e) => ({ name: e.condition, pnl: e.pnl }))}
                  layout="vertical"
                  margin={{ top: 4, right: 16, bottom: 4, left: 16 }}
                >
                  <CartesianGrid stroke="rgba(120,120,140,0.15)" />
                  <XAxis type="number" tickFormatter={(v) => formatPnl(v)} stroke="currentColor" fontSize={10} />
                  <YAxis dataKey="name" type="category" width={140} stroke="currentColor" fontSize={10} />
                  <Tooltip
                    formatter={(value: number) => formatPnl(value)}
                    contentStyle={{ fontSize: 11 }}
                  />
                  <Bar dataKey="pnl" fill="#059669" radius={[4, 4, 4, 4]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <p
            className="text-[11px] text-[var(--muted-text)] italic"
            data-testid="backtest-no-attribution"
          >
            Equity-curve time series is not provided by this backend endpoint.
            Showing aggregate metrics only.
          </p>
        )}
      </div>

      <div className="lg:col-span-4 space-y-4">
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic mb-4">
            Performance Summary
          </h3>
          <div className="space-y-3" data-testid="backtest-performance-summary">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="flex items-center justify-between py-2 border-b border-[var(--card-border)] last:border-0"
              >
                <span className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
                  {stat.label}
                </span>
                <span
                  className={cn('text-sm font-black italic', stat.color)}
                  data-testid={`backtest-stat-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {stat.value}
                </span>
              </div>
            ))}
          </div>
        </div>
        {report.recommendations && report.recommendations.length > 0 && (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic mb-3">
              Recommendations
            </h3>
            <ul className="space-y-2 text-xs text-[var(--muted-text)] list-disc pl-5">
              {report.recommendations.slice(0, 5).map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </div>
        )}
        <RuleMetadataPanel rules={result.rules} testIdPrefix="backtest" />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  Trade Replay
// ─────────────────────────────────────────────────────────────────

interface ReplayConfig {
  symbol: string;
  startDate: string;
  endDate: string;
}

function TradeReplaySimulator({
  status,
  result,
  error,
  selectedStrategy,
  onLoad,
}: {
  status: ReplayStatus;
  result: ReplayEnvelope | null;
  error: string | null;
  selectedStrategy: StrategyDraft | null;
  onLoad: (cfg: ReplayConfig) => void;
}) {
  const [symbol, setSymbol] = useState('AAPL');
  const [startDate, setStartDate] = useState('2024-01-01');
  const [endDate, setEndDate] = useState('2024-04-01');
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const canLoad = status !== 'running';

  const handleLoad = () => {
    if (!canLoad) return;
    setProgress(0);
    setIsPlaying(false);
    onLoad({
      symbol: symbol.trim() || 'AAPL',
      startDate,
      endDate,
    });
  };

  const togglePlay = () => {
    if (!result) return;
    setIsPlaying((p) => !p);
  };

  const handleRewind = () => {
    if (!result) return;
    setProgress((p) => Math.max(0, p - 10));
  };

  const handleFastForward = () => {
    if (!result) return;
    setProgress((p) => Math.min(100, p + 10));
  };

  React.useEffect(() => {
    if (!isPlaying || !result) return;
    const id = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          setIsPlaying(false);
          return 100;
        }
        return p + 5;
      });
    }, 500);
    return () => clearInterval(id);
  }, [isPlaying, result]);

  return (
    <div className="space-y-8" data-testid="trade-replay-root">
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic flex items-center gap-2">
            <Play size={20} className="text-emerald-600" /> Replay Controls
          </h3>
          <button
            type="button"
            onClick={handleLoad}
            disabled={!canLoad}
            aria-disabled={!canLoad}
            data-testid="strategy-btn-load-replay"
            className={cn(
              'px-6 py-2 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase italic transition-all shadow-lg shadow-emerald-600/20',
              canLoad ? 'hover:bg-emerald-700' : 'opacity-50 cursor-not-allowed',
            )}
          >
            {status === 'running' ? 'Loading...' : 'Load Replay'}
          </button>
        </div>
        {selectedStrategy ? (
          <p
            className="text-[11px] font-bold text-[var(--muted-text)] mb-4"
            data-testid="replay-selected-strategy"
          >
            Using rules from strategy <span className="text-[var(--app-text)]">"{selectedStrategy.name}"</span>
            {' '}({selectedStrategy.entry.length}E / {selectedStrategy.exit.length}X).
          </p>
        ) : (
          <p
            className="text-[11px] italic text-[var(--muted-text)] mb-4"
            data-testid="replay-no-strategy"
          >
            No strategy selected — replay will use the default simulator baseline.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
              Symbol
            </label>
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              data-testid="replay-input-symbol"
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-black text-[var(--app-text)] outline-none"
              maxLength={12}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
              Date Range
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="replay-input-start"
                className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-xs font-bold text-[var(--app-text)] outline-none"
              />
              <span className="text-[var(--muted-text)]">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="replay-input-end"
                className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-xs font-bold text-[var(--app-text)] outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      <ReplayResultsPanel
        status={status}
        result={result}
        error={error}
        isPlaying={isPlaying}
        progress={progress}
        onTogglePlay={togglePlay}
        onRewind={handleRewind}
        onFastForward={handleFastForward}
      />
    </div>
  );
}

function ReplayResultsPanel({
  status,
  result,
  error,
  isPlaying,
  progress,
  onTogglePlay,
  onRewind,
  onFastForward,
}: {
  status: ReplayStatus;
  result: ReplayEnvelope | null;
  error: string | null;
  isPlaying: boolean;
  progress: number;
  onTogglePlay: () => void;
  onRewind: () => void;
  onFastForward: () => void;
}) {
  if (status === 'running') {
    return (
      <div
        data-testid="replay-status-running"
        className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm min-h-[240px] flex flex-col items-center justify-center gap-3"
      >
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-xs font-bold text-[var(--muted-text)] uppercase">
          Loading replay...
        </p>
      </div>
    );
  }

  if (status === 'unavailable' || status === 'error') {
    return (
      <div
        data-testid="replay-status-unavailable"
        className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm min-h-[240px] flex flex-col items-center justify-center gap-3 text-[var(--muted-text)]"
      >
        <ShieldOff size={28} />
        <p className="text-xs font-bold uppercase">
          Trade Replay is not connected yet
        </p>
        {error && (
          <p className="text-[11px] font-medium max-w-md text-center">
            {error}
          </p>
        )}
      </div>
    );
  }

  if (status === 'idle' || !result) {
    return (
      <div
        data-testid="replay-status-empty"
        className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm min-h-[240px] flex flex-col items-center justify-center gap-3"
      >
        <Play className="text-[var(--muted-text)] opacity-30" size={40} />
        <p className="text-xs font-bold text-[var(--muted-text)] uppercase">
          No replay loaded
        </p>
        <p className="text-[11px] text-[var(--muted-text)]">
          Click <strong>Load Replay</strong> above.
        </p>
      </div>
    );
  }

  const eventTypeRows = Object.entries(result.eventTypes).sort(
    (a, b) => b[1] - a[1],
  );
  const currentEventIdx = Math.floor((progress / 100) * result.timelineCount);

  return (
    <div className="space-y-6">
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        {result.isFixture && (
          <div
            className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] font-bold text-amber-700 mb-4"
            data-testid="replay-fixture-banner"
          >
            <Info size={14} className="mt-0.5" />
            <span>
              Backend returned a sample fixture timeline. Replay walks a demo
              session, not live broker history.
            </span>
          </div>
        )}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic">
              Replay Session
            </h3>
            <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
              Loaded {formatRelative(result.fetchedAt)} —{' '}
              {result.timelineCount} timeline events
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onRewind}
              data-testid="replay-btn-rewind"
              className="p-3 bg-[var(--input-bg)] border border-[var(--input-border)] text-[var(--app-text)] rounded-xl hover:bg-[var(--card-border)] transition-all"
              aria-label="Rewind"
            >
              <Rewind size={18} />
            </button>
            <button
              type="button"
              onClick={onTogglePlay}
              data-testid="replay-btn-play"
              className="p-4 bg-emerald-600 rounded-xl hover:bg-emerald-500 transition-all shadow-lg shadow-emerald-600/20"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={22} className="text-white" /> : <Play size={22} className="fill-white" />}
            </button>
            <button
              type="button"
              onClick={onFastForward}
              data-testid="replay-btn-fast-forward"
              className="p-3 bg-[var(--input-bg)] border border-[var(--input-border)] text-[var(--app-text)] rounded-xl hover:bg-[var(--card-border)] transition-all"
              aria-label="Fast forward"
            >
              <FastForward size={18} />
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.2em] text-[var(--muted-text)]">
            <span>0</span>
            <span className="text-emerald-600">
              Event {currentEventIdx} / {result.timelineCount}
            </span>
            <span>{result.timelineCount}</span>
          </div>
          <div className="w-full h-2 bg-[var(--input-bg)] rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
              data-testid="replay-progress-bar"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-2">
            Events Processed
          </div>
          <div className="text-3xl font-black italic text-[var(--app-text)]">
            {result.events ?? result.timelineCount}
          </div>
        </div>
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-2">
            Blocked Trades
          </div>
          <div className="text-3xl font-black italic text-rose-600">
            {result.blockedTrades ?? 0}
          </div>
        </div>
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-2">
            Event Types
          </div>
          <ul className="space-y-1 text-xs font-bold text-[var(--app-text)]">
            {eventTypeRows.length === 0 ? (
              <li className="text-[var(--muted-text)]">—</li>
            ) : (
              eventTypeRows.slice(0, 4).map(([t, n]) => (
                <li key={t} className="flex justify-between">
                  <span>{t}</span>
                  <span className="text-[var(--muted-text)]">{n}</span>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      {result.reasonCodes.length > 0 && (
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <h4 className="text-sm font-black text-[var(--app-text)] uppercase italic mb-3">
            Block / Filter Reasons
          </h4>
          <ul className="space-y-2 text-xs text-[var(--muted-text)] list-disc pl-5">
            {result.reasonCodes.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}
      <RuleMetadataPanel rules={result.rules} testIdPrefix="replay" />
    </div>
  );
}
